import moment from "moment";

/**
 * Checks if a given value is a valid Date object.
 * @param value - The value to check.
 * @returns True if the value is a valid Date.
 */
export const isValidDate = (value: any): boolean => value instanceof Date && !isNaN(value.getTime());

/**
 * Checks whether a value is a valid Date object or a parsable date string.
 *
 * A value is considered valid if it's a Date instance with a valid timestamp,
 * or a string that can be parsed into a valid Date.
 *
 * @param value - The value to check (Date or string).
 * @returns True if the value is a valid date or parsable string, false otherwise.
 */
export const isParsableDate = (value: any): boolean => {
    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }

    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return !isNaN(parsedDate.getTime());
    }

    return false;
};

/**
 * Formats a date into a locale-specific string.
 *
 * When the optional `time` parameter is true, time components are included.
 *
 * @param date - The date (or date string) to format.
 * @param time - Whether to include time components.
 * @returns The formatted date string.
 */
export function dateFormatter(date: Date | string | undefined, time: boolean = false): string {
    if (!date || typeof date === "undefined") {
        return "";
    }

    const options: Intl.DateTimeFormatOptions = time
        ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
        : { day: "2-digit", month: "2-digit", year: "numeric" };

    return parseUtcDate(date).toLocaleDateString("et-ee", options);
}

/**
 * Formats a given date into a string showing only the month and year, 
 * using Estonian locale formatting.
 *
 * - If `isShort` is `true`, the format will be "MM.YYYY" (e.g., "05.2024").
 * - If `isShort` is `false`, the format will be "Month YYYY" (e.g., "mai 2024").
 *
 * @param {Date | string | undefined} date - The date to format. Can be a `Date` object, a valid date string, or `undefined`.
 * @param {boolean} [isShort=false] - Whether to use the short format. Defaults to `false`.
 * @returns {string} A formatted date string, or an empty string if the input is invalid.
 */
export const dateFormatMonthYear = (date: Date | string | undefined, isShort: boolean = false): string => {
    if (!date || typeof date === "undefined") {
        return "";
    }

    const options: Intl.DateTimeFormatOptions = isShort
        ? { month: '2-digit', year: 'numeric' }
        : { month: 'long', year: 'numeric' };

    return parseUtcDate(date).toLocaleDateString("et-ee", options);
};

/**
 * Validates a date range by checking if both dates are valid and the start date is not after the end date.
 *
 * @param {Date | string} start - The start date of the range (can be a Date object or an ISO string).
 * @param {Date | string} end - The end date of the range (can be a Date object or an ISO string).
 * @returns {boolean} Returns `true` if both dates are valid and the start date is earlier than to the end date; otherwise, returns `false`.
 *
 * @example
 * isValidDateRange("2023-01-01", "2023-12-31"); // true
 * isValidDateRange("2023-12-31", "2023-01-01"); // false
 */
export const isValidDateRange = (start: Date | string, end: Date | string): boolean => {
    const startDate = parseUtcDate(start);
    const endDate = parseUtcDate(end);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
    }

    return startDate < endDate;
};

/** UTC Conversion */
export const setUTCTime = (date: Date | string): Date | null => isValidDate(date) ? moment.utc(date).toDate() : null;
export const parseUtcDate = (date: Date | string): Date => moment.utc(new Date(date)).toDate();

/** Adjusts the timezone offset for a given date 
 *  @param date - The date to adjust (Date object or string).
 *  @param addOffset - If true, applies the timezone offset; if false, removes it.
 *  @returns The adjusted Date object or undefined if invalid.
 */
export const adjustTimeZoneOffset = (date: Date | string, addOffset: boolean = true): Date | undefined => {
    const _date = isValidDate(date) ? (date as Date) : new Date(date);
    if (!isValidDate(_date)) return undefined;

    const offsetInMilliseconds = _date.getTimezoneOffset() * 60 * 1000;
    return new Date(_date.getTime() + (addOffset ? offsetInMilliseconds : -offsetInMilliseconds));
};

export const removeTimeZoneOffset = (date: Date | string): Date | undefined => adjustTimeZoneOffset(date, false);

export const applyTimeZoneOffset = (date: Date | string): Date | undefined => adjustTimeZoneOffset(date, true);


/** Date Difference Calculation */
export const daysBetween = (startDate: Date | string | undefined | null, endDate: Date | string | undefined | null): number | null => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

/** Date Sorting */
export const sortDateAsc = (start: Date, end: Date): number => start.getTime() - end.getTime();
export const sortDateDesc = (start: Date, end: Date): number => end.getTime() - start.getTime();

/** First & Last Day of Month */
export const getNextMonthFirstDay = (date?: Date): Date => new Date((date ?? new Date()).getFullYear(), (date ?? new Date()).getMonth() + 1, 1);
export const getMonthLastDay = (date?: Date): Date => new Date((date ?? new Date()).getFullYear(), (date ?? new Date()).getMonth() + 1, 0);
/** Days in Month */
export const daysInMonth = (month: number, year: number): number => new Date(year, month, 0).getDate();

/**
 * Returns a string representing the difference between two dates in years, months, and days.
 *
 * The translation function `t` is used to get localized text for each segment.
 *
 * @param dateFrom - The starting date.
 * @param dateTo - The ending date.
 * @param t - A translation function that receives a key and returns a localized string.
 * @returns A formatted string of the date difference.
 */
export function dateDiffStr(dateFrom: Date, dateTo: Date, t: (key: string) => string): string {
    if (!isValidDate(dateFrom) || !isValidDate(dateTo)) return "";

    let a = moment(dateTo);
    let b = moment(dateFrom);

    const years = a.diff(b, "year");
    b.add(years, "years");

    const months = a.diff(b, "months");
    b.add(months, "months");

    const days = a.diff(b, "days");

    let dateStr = "";
    if (years !== 0) {
        dateStr += years + t("pages:profile.dateDiff.years");
    }
    if (months !== 0) {
        dateStr += (dateStr ? ", " : "") + months + t("pages:profile.dateDiff.months");
    }
    if (days !== 0) {
        dateStr += (dateStr ? ", " : "") + days + t("pages:profile.dateDiff.days");
    }
    return dateStr;
}


/**
 * Converts a given Date object to an ISO 8601 string in the format `YYYY-MM-DDT00:00:00.000Z`.
 * The resulting string represents midnight (00:00:00) of the specified date in UTC, ensuring
 * that the date part remains unchanged irrespective of the user's local timezone.
 *
 * @param {Date} date - The Date object to format. If no valid date is provided, an empty string is returned.
 * @returns {string} The formatted ISO 8601 string representing the date in UTC, or an empty string if the input is invalid.
 */
export function formatDateToISOString(date: Date): string {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * Converts a date string or Date object to the 'yyyy-MM-dd' format.
 * @param date - The date to format.
 * @returns The formatted date string or an empty string if invalid.
 */
export const toHtmlDateFormat = (date: Date | string | undefined | null): string => {
    if (!date) return "";
    const validDate = new Date(date);
    return isNaN(validDate.getTime()) ? "" : validDate.toISOString().split("T")[0];
};

export const toDate = (value: Date | string): Date | null => {
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Normalizes a Date object used for month-year selection by setting the time to noon (12:00).
 * 
 * This avoids timezone-related issues where a midnight timestamp might be interpreted
 * as the previous day in certain time zones (e.g., GMT+2 shifting to the previous day in UTC).
 * 
 * @param {Date} date - The original Date object representing a selected month and year.
 * @returns {Date} - A new Date object with the time set to 12:00 to prevent date shifting.
 */
export const normalizeMonthYear = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(12, 0, 0, 0); // Set to noon
    return d;
};